import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  GuardResult,
  MaybeAsync,
  Router,
} from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Permission } from '../../authorization/definitions/permission.enum';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { AuthorizationService } from '../services/authorization.service';

const unauthenticatedPaths = [
  'agreement',
  'debrief',
  'login',
  'reset-password',
];

@Injectable({ providedIn: 'root' })
export class AuthorizedGuard implements CanActivate {
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _authorizationService = inject(AuthorizationService);
  private readonly _router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): MaybeAsync<GuardResult> {
    const requiredPermissions: Array<Permission> =
      route.data?.permissions ?? [];

    const fullPath: Array<string> = [];
    route.pathFromRoot.forEach(routeSnapshot => {
      routeSnapshot.url.forEach(urlSegment => {
        fullPath.push(urlSegment.path);
      });
    });

    if (fullPath.length && fullPath[0] === 'logout') {
      this._authenticationService.requestLogout();
      return true;
    } else {
      return new Observable(observer => {
        this._authenticationService
          .getIsLoggedIn$()
          .pipe(take(1))
          .subscribe(authenticated => {
            if (!authenticated) {
              // User is not logged in. Redirect to Login page if necessary
              // and prevent authorization of requested route.
              if (
                fullPath.length &&
                unauthenticatedPaths.includes(fullPath[0])
              ) {
                observer.next(true);
                observer.complete();
              } else {
                observer.next(this._router.createUrlTree(['/login']));
                observer.complete();
              }
            } else {
              // User is logged in, must validate token.
              this._authenticationService.validateToken().subscribe({
                next: () => {
                  // Redirect user to default page if they are already logged in
                  // and attempt to navigate to an unauthenticated page.
                  if (
                    fullPath.length &&
                    unauthenticatedPaths.includes(fullPath[0])
                  ) {
                    observer.next(this._router.createUrlTree(['/']));
                    observer.complete();
                  } else if (!requiredPermissions.length) {
                    // Route does not specify permissions, therefore it's
                    // available to all users.
                    observer.next(true);
                    observer.complete();
                  } else {
                    forkJoin(
                      requiredPermissions.map(permission =>
                        this._authorizationService
                          .authenticatedUserCan(permission)
                          .pipe(take(1)),
                      ),
                    ).subscribe(results => {
                      const isAllowed = results.includes(true);
                      if (!isAllowed) {
                        observer.next(
                          this._router.createUrlTree(['/unauthorized']),
                        );
                        observer.complete();
                      } else {
                        observer.next(isAllowed);
                        observer.complete();
                      }
                    });
                  }
                },
                error: () => {
                  observer.next(this._router.createUrlTree(['/logout']));
                  observer.complete();
                },
              });
            }
          });
      });
    }
  }
}
